/**
 * The span's own start/end time labels above the day-strip rail — the span
 * tells its story without reading the legend. Narrow spans collapse to one
 * combined label. Danish first, like every other time (QA 18/10/2026): the
 * rail is the country's day, so the country's time follows its flag.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import { dkLabel } from '../../utils/clock/dk-label';
import { minutesLabel } from '../../utils/format';
import { useCountryClock, type CountryClock } from './time/country-clock-context';

/** With both clocks a label is ~2.5× wider — below this span, one combined label. */
const NARROW_ONE_CLOCK = 170;
const NARROW_TWO_CLOCKS = 420;

const pctOf = (min: number) =>
  Math.min(98, Math.max(2, ((((min % 1440) + 1440) % 1440) / 1440) * 100));

const dkOf = (min: number, c: CountryClock) => dkLabel(min, c.tz, c.runDate);

function oneLabel(min: number, c: CountryClock): string {
  const dk = dkOf(min, c);
  return dk ? `${dk} 🇩🇰 · ${c.flag} ${minutesLabel(min)}` : minutesLabel(min);
}

function rangeLabel(from: number, to: number, c: CountryClock): string {
  const local = `${minutesLabel(from)}–${minutesLabel(to)}`;
  const a = dkOf(from, c);
  const b = dkOf(to, c);
  if (!a || !b) return local;
  // A " −1d" mark must not glue to the dash (Bugbot on #274): spaced, like windowText.
  const dash = a.includes('d') || b.includes('d') ? ' – ' : '–';
  return `${a}${dash}${b} 🇩🇰 · ${c.flag} ${local}`;
}

const SLIDE = 'absolute whitespace-nowrap transition-[left] duration-500 ease-out motion-reduce:transition-none';

/** Centred on its time, but kept inside the rail near either edge. */
const anchor = (pct: number) => (pct < 15 ? 'translate-x-0' : pct > 85 ? '-translate-x-full' : '-translate-x-1/2');

const At: FC<{ min: number; children: string }> = ({ min, children }) => (
  <span className={`${SLIDE} ${anchor(pctOf(min))}`} style={{ left: `${pctOf(min)}%` }}>
    {children}
  </span>
);

export const SpanLabels: FC<{ startMin: number; endMin: number }> = ({ startMin, endMin }) => {
  const clock = useCountryClock();
  const narrow = endMin - startMin < (clock.tz ? NARROW_TWO_CLOCKS : NARROW_ONE_CLOCK);
  return (
    <div className="relative h-3.5 font-mono text-[10px] font-medium tabular-nums text-primary">
      {narrow ? (
        <At min={(startMin + endMin) / 2}>{rangeLabel(startMin, endMin, clock)}</At>
      ) : (
        <>
          <At min={startMin}>{oneLabel(startMin, clock)}</At>
          <At min={endMin}>{oneLabel(endMin, clock)}</At>
        </>
      )}
    </div>
  );
};
